import { Link } from 'react-router-dom'
import { Heart } from 'lucide-react'
import { Button, Card, CardContent, Badge, StatusBadge } from '@/components/ui'
import { useFavoritesStore } from '@/stores/favorites'
import { getVehicleById } from '@/data/vehicles'
import { formatPrice, formatMileage } from '@/utils/format'

export function FavoritesPage() {
  const { favorites, removeFavorite } = useFavoritesStore()

  // Get vehicle data for each favorite
  const savedVehicles = favorites
    .map((id) => getVehicleById(id))
    .filter(Boolean)

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">My Favorites</h1>
          <p className="mt-2 text-gray-600">
            {savedVehicles.length > 0
              ? `You have ${savedVehicles.length} saved vehicle${savedVehicles.length === 1 ? '' : 's'}`
              : 'Save vehicles you love to find them easily later'}
          </p>
        </div>

        {savedVehicles.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <Heart className="mx-auto h-16 w-16 text-gray-300" />
              <h2 className="mt-6 text-xl font-semibold text-gray-900">
                No favorites yet
              </h2>
              <p className="mt-2 text-gray-600 max-w-md mx-auto">
                Browse our inventory and tap the heart icon to save vehicles you like.
              </p>
              <Link to="/vehicles" className="inline-block mt-6">
                <Button size="lg">Browse Vehicles</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {savedVehicles.map((vehicle) => {
                if (!vehicle) return null
                const title = `${vehicle.year} ${vehicle.make} ${vehicle.model}`
                const isAvailable = vehicle.status === 'available'

                return (
                  <Card key={vehicle.id} className="overflow-hidden">
                    {/* Image */}
                    <div className="relative aspect-[4/3] bg-gray-100">
                      {vehicle.photos[0] ? (
                        <img
                          src={vehicle.photos[0]}
                          alt={title}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center">
                          <span className="text-gray-300">No Image</span>
                        </div>
                      )}
                      {/* Badges */}
                      <div className="absolute left-2 top-2 flex gap-2">
                        {vehicle.certified && (
                          <Badge variant="primary" size="sm">
                            Certified
                          </Badge>
                        )}
                        {!isAvailable && (
                          <StatusBadge status={vehicle.status} size="sm" />
                        )}
                      </div>
                      {/* Remove button */}
                      <button
                        onClick={() => removeFavorite(vehicle.id)}
                        className="absolute right-2 top-2 rounded-full bg-white/90 p-2 text-red-500 shadow-sm transition-colors hover:bg-white"
                        aria-label="Remove from favorites"
                      >
                        <Heart className="h-5 w-5 fill-current" />
                      </button>
                    </div>

                    {/* Content */}
                    <CardContent>
                      <Link
                        to={`/vehicles/${vehicle.id}`}
                        className="hover:text-primary-600"
                      >
                        <h3 className="text-lg font-semibold text-gray-900">
                          {title}
                        </h3>
                      </Link>
                      <p className="text-sm text-gray-600">{vehicle.trim}</p>
                      <p className="mt-2 text-2xl font-bold text-gray-900">
                        {formatPrice(vehicle.priceCents)}
                      </p>
                      <p className="mt-1 text-sm text-gray-600">
                        {formatMileage(vehicle.mileage)}
                      </p>

                      {/* Actions */}
                      <div className="mt-4 flex gap-2">
                        <Link to={`/vehicles/${vehicle.id}`} className="flex-1">
                          <Button variant="outline" className="w-full">
                            View Details
                          </Button>
                        </Link>
                        {isAvailable && (
                          <Link to={`/purchase/${vehicle.id}`} className="flex-1">
                            <Button className="w-full">
                              Start Purchase
                            </Button>
                          </Link>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </div>

            {/* Continue Shopping */}
            <Card className="mt-8">
              <CardContent className="flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
                <div>
                  <h3 className="font-semibold text-gray-900">
                    Looking for more options?
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    Continue browsing our full inventory
                  </p>
                </div>
                <Link to="/vehicles">
                  <Button>Browse All Vehicles</Button>
                </Link>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  )
}
